import { useContext } from 'react';
// @dynamic-i18n-keys
//   navigation.subnavigation.watchGroup
//   navigation.subnavigation.unwatchGroup
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Url } from '@wix/da-url';
import { BiData, GiftSelectClickBiEvent } from '@wix/da-bi/pkg/events';
import { actions as modalActions } from '@wix/da-shared-react/pkg/Modals';
import {
  pushModal,
  popModal,
} from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { clickWatch } from '@wix/da-shared-react/pkg/WatchButton/redux/actionCreators';
import { getUserWatchState } from '@wix/da-shared-react/pkg/WatchButton/redux/selectors';
import {
  requestJoinGroup,
  messageAdmins as messageAdminsAction,
} from '@wix/da-shared-react/pkg/redux/groups/actions';
import { ModalType as SharedModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import {
  inviteToGroup,
  requestCloseGroup,
  requestChangeFounder,
  requestFounderLeaveGroup,
} from '../../../../../../actions/groups';
import {
  getProfileOwner,
  getProfileOwnerUser,
  getIsGroupProfile,
  isCurrentUserProfileOwner,
} from '../../../../../../selectors/users';
import { getIsGroupMember } from '../../../../../../selectors/groups';
import {
  getCanJoinGroup,
  getCanLeaveGroup,
  getCanInviteToGroup,
  getCanCloseGroup,
  getCanChangeFounder,
  getCanMessageAdmins,
  getCanRespondToNotifications,
} from '../../../../../../selectors/privs';
import { SectionContext } from '../../../../../Sections/SectionContext';
import { fetchProfileOwnerGiveStateSuccess } from '../../../../../../actions/profileOwner';
import { pushGivePointsModal } from '@wix/da-shared-react/pkg/gifting/redux/actions';
import { ModalType } from '../../../../../../../types/modals';
import { useCopyProfileLinkClick } from '../../../../../../utils/useCopyProfileLinkClick';
import { useRestrictToLoggedIn } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedIn';
import { ReferralType } from '@wix/da-bi/pkg/constants';

interface Item {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  biData?: any;
}

export default function useMoreActionItems() {
  const { t } = useTranslation();
  const section = useContext(SectionContext);
  const isBackroomSection = section === 'backroom';
  const { restrictToLoggedIn } = useRestrictToLoggedIn();

  // Selectors
  const giveState = useSelector(getProfileOwner).giveState;
  const isOwner = useSelector(isCurrentUserProfileOwner);
  const isGroup = useSelector(getIsGroupProfile);
  const profileOwnerUser = useSelector(getProfileOwnerUser);
  const isWatching = useSelector(
    state => getUserWatchState(state, profileOwnerUser).isWatching
  );

  // Privileges
  const isMember = useSelector(getIsGroupMember);
  const canJoinGroup = useSelector(getCanJoinGroup);
  const canLeaveGroup = useSelector(getCanLeaveGroup);
  const canInviteToGroup = useSelector(getCanInviteToGroup);
  const canCloseGroup = useSelector(getCanCloseGroup);
  const canChangeFounder = useSelector(getCanChangeFounder);
  const canMessageAdmins = useSelector(getCanMessageAdmins);
  const canRespondToNotifications = useSelector(getCanRespondToNotifications);
  // Leave group item has 1 label, and 3 different flows
  // 1. If canLeaveGroup, just show the leave group flow
  // 2. If !canLeaveGroup && canChangeFounder && !isBackroomSection - this
  //    means that the user is the founder of the group and cannot leave, they
  //    need to change the group founder, but cannot, since changing the founder
  //    can only be done in the backroom section, where the recruitment module
  //    lives, so we default to shared requestLeaveGroup action which will show
  //    the notification explaining this and redirect to group backroom
  // 3. If !canLeaveGroup && canChangeFounder && isBackroomSection - show the changeFounder flow
  // This covers 1. and 2.
  const showSharedLeaveGroup =
    canLeaveGroup || (canChangeFounder && !isBackroomSection);
  // And this covers 3.
  const showChangeFounder =
    !canLeaveGroup && canChangeFounder && isBackroomSection;

  // Actions
  const dispatch = useDispatch();
  const pushBadgeModal = (
    username: string,
    onBadgeSent?: () => void,
    onClose?: () => void,
    badgeType?: string
  ) =>
    dispatch(
      modalActions.pushBadgeModal(username, onBadgeSent, onClose, badgeType)
    );

  const dispatchPopModal = () => dispatch(popModal());

  const onPushPointsModal = (username, title) =>
    dispatch(pushGivePointsModal(username, title));
  const onLlamaBadgeSent = () =>
    dispatch(fetchProfileOwnerGiveStateSuccess({ canBeGivenLlama: false }));
  const onCakeBadgeSent = () =>
    dispatch(fetchProfileOwnerGiveStateSuccess({ canBeGivenCake: false }));
  const requestJoin = () => dispatch(requestJoinGroup(profileOwnerUser));
  const blockUser = () =>
    dispatch(
      pushModal(SharedModalType.USER_BLOCK, {
        params: {
          user: profileOwnerUser,
          redirectLocation: Url.browseHomeLink(),
        },
      })
    );
  const changeFounder = () => dispatch(requestChangeFounder(profileOwnerUser));
  const leaveGroup = () => dispatch(requestFounderLeaveGroup(profileOwnerUser));
  const closeGroup = () => dispatch(requestCloseGroup(profileOwnerUser));
  const inviteUsers = () => dispatch(inviteToGroup(profileOwnerUser));
  const toggleWatch = () => dispatch(clickWatch(profileOwnerUser, isWatching));
  const messageAdmins = () => dispatch(messageAdminsAction(profileOwnerUser));
  const openNotifications = () =>
    window.location.assign(Url.groupNotificationsLink(profileOwnerUser.userId));
  const { onCopyLinkClick } = useCopyProfileLinkClick(profileOwnerUser);

  // Items
  let items: Item[] = [];
  if (isGroup) {
    return [
      isMember && {
        label: t(
          `navigation.subnavigation.${
            isWatching ? 'unwatchGroup' : 'watchGroup'
          }`
        ),
        onClick: toggleWatch,
      },
      showSharedLeaveGroup && {
        label: t('navigation.subnavigation.leaveGroup'),
        onClick: leaveGroup,
      },
      showChangeFounder && {
        label: t('navigation.subnavigation.leaveGroup'),
        onClick: changeFounder,
      },
      canRespondToNotifications && {
        label: t('navigation.subnavigation.groupNotifications'),
        onClick: openNotifications,
      },
      canInviteToGroup && {
        label: t('navigation.subnavigation.sendInvitation'),
        onClick: inviteUsers,
      },
      canCloseGroup && {
        label: t('navigation.subnavigation.closeGroup'),
        onClick: closeGroup,
      },
      canMessageAdmins && {
        label: t('navigation.subnavigation.messageAdmins'),
        onClick: messageAdmins,
      },
      canJoinGroup && {
        label: t('navigation.subnavigation.requestJoin'),
        onClick: requestJoin,
      },
    ].filter(Boolean) as Item[];
  }
  if (!isOwner) {
    if (!giveState) {
      return [];
    }
    items = [
      {
        label: giveState.canBeGivenCore
          ? t('navigation.subnavigation.giveCore')
          : t('navigation.subnavigation.hasCore'),
        onClick: () =>
          window.location.assign(
            Url.giveCoreMembership(profileOwnerUser.username)
          ),
        disabled: !giveState.canBeGivenCore,
        biData: BiData<GiftSelectClickBiEvent>({
          evid: 275,
          menu_item_name: giveState.canBeGivenCore
            ? t('navigation.subnavigation.giveCore')
            : t('navigation.subnavigation.hasCore'),
        }),
      },
      {
        label: t('navigation.subnavigation.givePoints'),
        onClick: () => {
          onPushPointsModal(
            profileOwnerUser.username,
            t('navigation.subnavigation.givePoints')
          );
        },
      },
      {
        label: giveState.canBeGivenLlama
          ? t('navigation.subnavigation.giveLlamaBadge')
          : t('navigation.subnavigation.hasGotLlamaBadge'),
        onClick: () => {
          pushBadgeModal(
            profileOwnerUser.username,
            onLlamaBadgeSent,
            dispatchPopModal,
            'llama'
          );
        },
        disabled: !giveState.canBeGivenLlama,
      },
      {
        label: giveState.canBeGivenCake
          ? t('navigation.subnavigation.giveCakeBadge')
          : t('navigation.subnavigation.hasGotCakeBadge'),
        onClick: () => {
          pushBadgeModal(
            profileOwnerUser.username,
            onCakeBadgeSent,
            dispatchPopModal,
            'cake'
          );
        },
        disabled: !giveState.canBeGivenCake,
      },
    ];

    items.push({
      label: t('navigation.subnavigation.reportUser'),
      onClick: restrictToLoggedIn(ReferralType.REPORT, () => {
        dispatch(
          pushModal(ModalType.REPORT_PROFILE, {
            wrapInModal: false,
            params: { userId: profileOwnerUser.userId },
          })
        );
      }),
    });
  }

  if (!isOwner) {
    items.push({
      label: t('navigation.subnavigation.blockUser'),
      onClick: blockUser,
    });
  }

  items.push({
    label: t('navigation.subnavigation.copyLink'),
    onClick: onCopyLinkClick,
  });

  return items;
}
